import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

export enum TransitionType {
  Fade = 'Fade',
  SlideInFromLeft = 'SlideInFromLeft',
  SlideInFromBottom = 'SlideInFromBottom',
  ZoomIn = 'ZoomIn',
  ZoomOutReveal = 'ZoomOutReveal',
  ScaleBounce = 'ScaleBounce',
  RotateIn = 'RotateIn',
  FlipIn3D = 'FlipIn3D',
  SpinIn = 'SpinIn',
  OvershootSlide = 'OvershootSlide',
}

const transitionInstructions = {
  [TransitionType.Fade]: {
    description: 'Simple opacity fade-in or fade-out',
    instructions: ` Fade-In
At t=0:   opacity =   0%   interpolation = linear
At t=1:   opacity = 100%   interpolation = easing

// Fade-Out (reverse)
At t=0:   opacity = 100%   interpolation = easing
At t=1:   opacity =   0%   interpolation = linear`,
  },
  [TransitionType.SlideInFromLeft]: {
    description: 'Layer glides horizontally from off-screen left while fading in',
    instructions: `// In
At t=0:
  position = (finalPositionX-compositionWidth, finalPositionY)
  opacity  =   0%
  interp   = linear
At t=1:
  position = (finalPositionX, 0)
  opacity  = 100%
  interp   = easing

// Out (slide to right & fade)
  Reverse the position movement and opacity change but this time start with easing`,
  },
  [TransitionType.SlideInFromBottom]: {
    description: 'Layer rises from below while fading in',
    instructions: `// In
At t=0:
  position = (finalPositionX, finalPositionY+compositionHeight)
  opacity  =   0%
  interp   = linear
At t=1:
  position = (finalPositionX, finalPositionY)
  opacity  = 100%
  interp   = easing

// Out (slide down & fade)
Invert Y offsets, this time start with easing`,
  },
  [TransitionType.ZoomIn]: {
    description: 'Layer scales up from slightly smaller while fading in',
    instructions: `// In
At t=0:
  scale    = (0.8*finalScaleX, 0.8*finalScaleY)
  anchor   =  center (each type of layer require different value to make the anchor centered)
  opacity  =   0%
  interp   = linear
At t=1:
  scale    = (finalScaleX, finalScaleY)
  opacity  = 100%
  interp   = easing

// Out (zoom-out & fade)
Start at scale 1.0, opacity 100% → end at scale 0.8, opacity 0% with easeIn`,
  },
  [TransitionType.ZoomOutReveal]: {
    description: 'Layer starts large and scales down into place, giving a reveal feeling',
    instructions: `// In
At t=0:
  scale   = (1.2*finalScaleX, 1.2*finalScaleY)
  opacity =   0%
  interp  = linear
At t=0.7:
  opacity = 100%
  interp  = easing
At t=1:
  scale   = (finalScaleX, finalScaleY)
  interp  = easing

// Out (scale up & fade)
Mirror keyframes in reverse order, this time start the scale with easing`,
  },
  [TransitionType.ScaleBounce]: {
    description: 'Quick grow-overshoot then settle (gives pop)',
    instructions: `// In
At t=0:     scale = (0.5*finalScaleX, 0.5*finalScaleY)     interp = linear
At t=0.6:   scale = (1.1*finalScaleX, 1.1*finalScaleY)     interp = easing
At t=0.8:   scale = (0.9*finalScaleX, 0.9*finalScaleY)     interp = easeIn
At t=1:     scale = (finalScaleX, finalScaleY)     interp = easing

// Out (pop-out)
Reverse times & values`,
  },
  [TransitionType.RotateIn]: {
    description: 'Layer swings into view via 2D rotation around Z-axis',
    instructions: `// In
At t=0:
  rotationZ = -90°
  opacity  = 0%
  interp   = linear
At t=1:
  rotationZ =  0°
  opacity  = 100%
  interp   = easing

// Out (rotate away)
Reverse sign on angle and start with easing`,
  },
  [TransitionType.FlipIn3D]: {
    description: 'Layer appears to flip forward like a card',
    instructions: `// In
At t=0:
  rotationY = 90°      // facing away
  opacity  =   0%
  interp   = linear
At t=0.8:
  opacity  = 100%
  interp   = easing
At t=1:
  rotationY = 0°
  interp   = easeOut

// Out (flip backward)
Swap from 0 → 90° and fade out and start with easing`,
  },
  [TransitionType.SpinIn]: {
    description: 'Full spin while fading in at the end',
    instructions: `// In
At t=0:
  rotationZ =0°
  opacity  = 0%
  interp   = linear
At t=0.8:
  rotationZ = 360°
  interp   = linear
At t=1:
  opacity = 100%
  interp  = easeOut

// Out (spin out)
Reverse rotation direction & keyframe order, this time start with easing`,
  },
  [TransitionType.OvershootSlide]: {
    description: 'Slide in past its final position then settle back (polished feel)',
    instructions: `// In
At t=0:
  position = (FinalPositionX+CompositionWidth, 0)
  interp   = linear
At t=0.7:
  position = (FinalPositionX-20, 0)         // 20px past center
  interp   = easing in/out
At t=1:
  position = (0, 0)
  interp   = easing

// Out
Mirror with negative offsets & reverse easing`,
  },
};

const inputSchema = z.object({
  transitions: z
    .array(z.nativeEnum(TransitionType))
    .describe('Array of transitions to get information for'),
  isGetDescription: z.boolean().describe('Whether to include transition descriptions'),
  isGetInstructions: z.boolean().describe('Whether to include transition instructions'),
});

function getTransitionDescriptionAndInstructionsTool() {
  return tool({
    name: AdBuilderAgentToolName.GetTransitionDescriptionAndInstructions,
    displayName: 'Getting transition description and instructions',
    description:
      'Get descriptions and/or implementation instructions for various layer transitions. Avoid asking for too much info. if user ask for suggestion, only ask for instructions. ',
    inputSchema,
    execute: async ({
      input: {transitions, isGetDescription, isGetInstructions},
    }: ToolExecuteParams<typeof inputSchema>) => {
      const results = transitions.map((transitionType) => {
        const transition = transitionInstructions[transitionType];

        if (!transition) {
          throw new Error(`Unknown transition type: ${transitionType}`);
        }

        const parts: string[] = [];

        if (isGetDescription) {
          parts.push(`Description: ${transition.description}`);
        }

        if (isGetInstructions) {
          parts.push(`Instructions:\n${transition.instructions}`);
        }

        return `${transitionType}:\n${parts.join('\n\n')}`;
      });

      const content = results.join('\n\n---\n\n');

      // Add important note if instructions are included
      const finalContent = isGetInstructions
        ? content +
          '\n\nImportant:\n - Get the current layer Transform in order to understand their final required transform after transition in\n - t=0 and t=1 represent the start/end time of the transition in/out\n - Think for geometry reason before deciding final values to send to edit layer transform.'
        : content;

      return {
        content: finalContent,
      };
    },
  });
}

export default getTransitionDescriptionAndInstructionsTool;
